import { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Camera, Clock, X, Check, ChevronRight, Info } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'upload', label: 'Upload patient data' },
  { key: 'ccda', label: 'Import C-CDA' },
  { key: 'scan', label: 'Scan / Upload ID' },
];

const ImportPatientsModal = ({
  open,
  onOpenChange,
  onStartCSVUpload,
  onStartCCDAUpload,
  onStartScan,
  onViewHistory,
}) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [ccdaFiles, setCcdaFiles] = useState([]);
  const fileRef = useRef(null);
  const ccdaRef = useRef(null);
  const scanRef = useRef(null);

  useEffect(() => {
    if (open) {
      setActiveTab('upload');
      setFile(null);
      setCcdaFiles([]);
      setDragOver(false);
    }
  }, [open]);

  const getAccept = () => {
    if (activeTab === 'upload') return '.csv,.xlsx,.xls';
    if (activeTab === 'ccda') return '.xml,.ccda,.cda';
    return '.jpg,.jpeg,.png,.pdf,.heic';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (activeTab === 'ccda') {
      setCcdaFiles(dropped.slice(0, 10));
    } else {
      setFile(dropped[0] || null);
    }
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (activeTab === 'ccda') {
      setCcdaFiles(picked.slice(0, 10));
    } else {
      setFile(picked[0] || null);
    }
  };

  const loadSampleFile = async (filename) => {
    try {
      const res = await fetch(`/${filename}`);
      if (!res.ok) throw new Error('Failed to load');
      const blob = await res.blob();
      const sampleFile = new File([blob], filename, { type: blob.type });
      if (activeTab === 'ccda') {
        setCcdaFiles([sampleFile]);
      } else {
        setFile(sampleFile);
      }
      toast.success(`Sample file loaded: ${filename}`);
    } catch {
      toast.error('Could not load sample file');
    }
  };

  const handlePrimary = () => {
    if (activeTab === 'upload') {
      if (!file) return;
      onOpenChange(false);
      onStartCSVUpload?.(file);
    } else if (activeTab === 'ccda') {
      if (ccdaFiles.length === 0) return;
      onOpenChange(false);
      onStartCCDAUpload?.(ccdaFiles);
    } else if (activeTab === 'scan') {
      if (!file) return;
      onOpenChange(false);
      onStartScan?.(file);
    }
  };

  const handleMinimize = () => {
    onOpenChange(false);
  };

  if (!open) return null;

  const hasFile = activeTab === 'ccda' ? ccdaFiles.length > 0 : !!file;
  const primaryLabel = activeTab === 'upload' ? 'Upload' : activeTab === 'ccda' ? 'Import' : 'Upload';
  const selectedFileName = activeTab === 'ccda'
    ? (ccdaFiles.length > 0 ? `${ccdaFiles.length} file(s) selected` : null)
    : file?.name || null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      data-testid="import-patients-modal-overlay">
      {/* Overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
        onClick={() => onOpenChange(false)} />

      {/* Modal */}
      <div style={{
        position: 'relative', background: '#fff', borderRadius: 14, maxWidth: 620, width: '100%',
        border: '0.5px solid #e0e0e0', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', maxHeight: '85vh',
        animation: 'fadeIn 0.2s ease'
      }} data-testid="import-patients-modal">

        {/* Header */}
        <div style={{ padding: '18px 20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 500, color: '#111', margin: 0 }}>Import patients</h2>
              <p style={{ fontSize: 12, color: '#888', margin: '3px 0 0' }}>Add patient records from files, clinical documents, or ID scans</p>
            </div>
            <button onClick={() => onOpenChange(false)}
              style={{ width: 28, height: 28, borderRadius: '50%', background: '#f5f5f5', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              data-testid="import-modal-close">
              <X style={{ width: 14, height: 14, color: '#888' }} />
            </button>
          </div>
        </div>

        {/* Tab Row */}
        <div style={{ padding: '14px 20px 0', borderBottom: '0.5px solid #f0f0f0', marginTop: 14, display: 'flex', gap: 0 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); setFile(null); setCcdaFiles([]); }}
              style={{
                fontSize: 12, padding: '8px 16px', cursor: 'pointer', background: 'none', border: 'none',
                color: activeTab === t.key ? '#111' : '#aaa', fontWeight: activeTab === t.key ? 500 : 400,
                borderBottom: `2px solid ${activeTab === t.key ? '#111' : 'transparent'}`,
                marginBottom: '-0.5px', transition: 'color 0.15s',
              }}
              onMouseEnter={e => { if (activeTab !== t.key) e.currentTarget.style.color = '#555'; }}
              onMouseLeave={e => { if (activeTab !== t.key) e.currentTarget.style.color = '#aaa'; }}
              data-testid={`import-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', flex: 1 }}>

          {/* Tab Content */}
          <>
            {activeTab === 'upload' && <UploadTabContent
              file={file} dragOver={dragOver} setDragOver={setDragOver}
              onDrop={handleDrop} onFileChange={handleFileChange}
              fileRef={fileRef} onLoadSample={loadSampleFile} />}
            {activeTab === 'ccda' && <CCDATabContent
              files={ccdaFiles} dragOver={dragOver} setDragOver={setDragOver}
              onDrop={handleDrop} onFileChange={handleFileChange}
              fileRef={ccdaRef} onLoadSample={loadSampleFile} />}
            {activeTab === 'scan' && <ScanTabContent
              file={file} dragOver={dragOver} setDragOver={setDragOver}
              onDrop={handleDrop} onFileChange={handleFileChange}
              fileRef={scanRef} onLoadSample={loadSampleFile}
              onOpenCamera={() => { onOpenChange(false); onStartScan?.(null); }} />}
          </>
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '0.5px solid #f0f0f0', padding: '14px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <button onClick={() => { onOpenChange(false); onViewHistory?.(activeTab); }}
            style={{ fontSize: 11, color: '#888', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = '#555'}
            onMouseLeave={e => e.currentTarget.style.color = '#888'}
            data-testid="import-view-history-link">
            <Clock style={{ width: 11, height: 11 }} />
            View import history
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => onOpenChange(false)}
              style={{ height: 34, padding: '0 14px', border: '0.5px solid #e0e0e0', borderRadius: 7, background: '#fff', color: '#555', fontSize: 12, cursor: 'pointer' }}
              data-testid="import-cancel-btn">
              Cancel
            </button>
            <button onClick={handlePrimary} disabled={!hasFile && activeTab !== 'scan'}
              style={{
                height: 34, padding: '0 14px', background: hasFile || activeTab === 'scan' ? '#111' : '#ccc',
                color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 500, border: 'none',
                cursor: hasFile || activeTab === 'scan' ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
              data-testid="import-primary-btn">
              {primaryLabel}
              <span style={{ fontSize: 11, opacity: 0.7 }}>&rarr;</span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

/* ── Tab 1: Upload Patient Data ── */
const UploadTabContent = ({ file, dragOver, setDragOver, onDrop, onFileChange, fileRef, onLoadSample }) => (
  <>
    {/* Description */}
    <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8, fontSize: 12, color: '#666', lineHeight: 1.6, border: '0.5px solid #f0f0f0' }}
      data-testid="tab-upload-desc">
      Upload a CSV or spreadsheet with patient demographics, insurance, and contact information. Use the sample file below to see the expected format before uploading your own.
    </div>

    {/* Upload Zone */}
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => fileRef.current?.click()}
      style={{
        border: `1.5px dashed ${dragOver ? '#999' : '#d1d5db'}`,
        borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
        background: dragOver ? '#f9f9f9' : '#fff', transition: 'all 0.15s',
      }}
      data-testid="upload-tab-dropzone">
      {file ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Check style={{ width: 16, height: 16, color: '#16a34a' }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{file.name}</span>
        </div>
      ) : (
        <>
          <div style={{ width: 36, height: 36, background: '#f5f5f5', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
            <Upload style={{ width: 18, height: 18, color: '#555' }} />
          </div>
          <p style={{ fontSize: 13, fontWeight: 500, color: '#111', margin: '0 0 3px' }}>Drop your file here or click to browse</p>
          <p style={{ fontSize: 11, color: '#aaa', margin: 0 }}>Drag and drop or click to select from your computer</p>
          <p style={{ fontSize: 10, color: '#bbb', marginTop: 6 }}>Accepted: .csv, .xlsx, .xls</p>
        </>
      )}
      <input ref={fileRef} type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={onFileChange} style={{ display: 'none' }}
        data-testid="upload-tab-file-input" />
    </div>

    {/* Divider */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
      <span style={{ fontSize: 11, color: '#ccc' }}>or try a sample file</span>
      <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
    </div>

    {/* Sample Files */}
    <div>
      <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Sample files</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SampleRow
          icon={<FileText style={{ width: 14, height: 14, color: '#888' }} />}
          iconBg="#f0f0f0"
          name="sample_patients_template.csv"
          meta="100 example patients &middot; all required fields &middot; 4 KB"
          onTry={() => onLoadSample('sample_patients_template.csv')} />
      </div>
    </div>
  </>
);

/* ── Tab 2: Import C-CDA ── */
const CCDATabContent = ({ files, dragOver, setDragOver, onDrop, onFileChange, fileRef, onLoadSample }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <>
      {/* Description */}
      <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8, fontSize: 12, color: '#666', lineHeight: 1.6, border: '0.5px solid #f0f0f0' }}
        data-testid="tab-ccda-desc">
        <p style={{ margin: 0 }}>C-CDA files are XML clinical records exported from certified EHRs. They contain diagnoses, medications, allergies, and immunizations. All US EHRs are required to support this format.</p>
        <div style={{ position: 'relative', display: 'inline-block', marginTop: 6 }}>
          <button onClick={() => setShowTooltip(!showTooltip)}
            style={{ fontSize: 11, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            What is C-CDA?
          </button>
          {showTooltip && (
            <div style={{ position: 'absolute', bottom: '100%', left: 0, width: 300, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, marginBottom: 4 }}>
              <p style={{ fontSize: 11, color: '#555', lineHeight: 1.6, margin: 0 }}>
                C-CDA (Consolidated Clinical Document Architecture) is a standardized XML format for sharing clinical summaries between healthcare systems. It includes patient demographics, diagnoses, medications, allergies, immunizations, and more. Every certified EHR in the US can export data in this format.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `1.5px dashed ${dragOver ? '#999' : '#d1d5db'}`,
          borderRadius: 10, padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
          background: dragOver ? '#f9f9f9' : '#fff', transition: 'all 0.15s',
        }}
        data-testid="ccda-tab-dropzone">
        {files.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Check style={{ width: 16, height: 16, color: '#16a34a' }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{files.length} file(s) selected</span>
          </div>
        ) : (
          <>
            <div style={{ width: 36, height: 36, background: '#f5f5f5', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <FileText style={{ width: 18, height: 18, color: '#555' }} />
            </div>
            <p style={{ fontSize: 13, fontWeight: 500, color: '#111', margin: '0 0 3px' }}>Drop your file here or click to browse</p>
            <p style={{ fontSize: 11, color: '#aaa', margin: 0 }}>Drag and drop or click to select from your computer</p>
            <p style={{ fontSize: 10, color: '#bbb', marginTop: 6 }}>Accepted: .xml, .ccda, .cda</p>
          </>
        )}
        <input ref={fileRef} type="file" className="hidden" accept=".xml,.ccda,.cda" multiple onChange={onFileChange} style={{ display: 'none' }}
          data-testid="ccda-tab-file-input" />
      </div>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
        <span style={{ fontSize: 11, color: '#ccc' }}>or try a sample file</span>
        <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
      </div>

      {/* Sample Files */}
      <div>
        <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Sample files</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SampleRow
            icon={<FileText style={{ width: 14, height: 14, color: '#888' }} />}
            iconBg="#f0f0f0"
            name="sample_ccda_patient.xml"
            meta="Fictional patient &middot; medications, diagnoses, allergies &middot; 4 KB"
            onTry={() => onLoadSample('sample_ccda_patient.xml')} />
        </div>
      </div>

      {/* Batch note */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#aaa' }}>
        <Info style={{ width: 12, height: 12, flexShrink: 0 }} />
        <span>You can upload up to 10 XML files at once.</span>
      </div>
    </>
  );
};

/* ── Tab 3: Scan / Upload ID ── */
const ScanTabContent = ({ file, dragOver, setDragOver, onDrop, onFileChange, fileRef, onLoadSample, onOpenCamera }) => (
  <>
    {/* Description */}
    <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8, fontSize: 12, color: '#666', lineHeight: 1.6, border: '0.5px solid #f0f0f0' }}
      data-testid="tab-scan-desc">
      Use your camera to scan a patient's ID or insurance card. Lens extracts the name, date of birth, address, and insurance information automatically — no manual entry required.
    </div>

    {/* Scan Panel */}
    <div style={{ background: '#f9fafb', border: '0.5px solid #e8e8e8', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}
      data-testid="scan-panel">
      <div style={{ width: 40, height: 40, borderRadius: 8, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Camera style={{ width: 18, height: 18, color: '#555' }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: '#111', margin: 0 }}>Open camera to scan ID</p>
        <p style={{ fontSize: 11, color: '#666', margin: '2px 0 0' }}>Works with driver's license, passport, or insurance card</p>
      </div>
      <button onClick={onOpenCamera}
        style={{ height: 34, padding: '0 14px', background: '#6b5ce7', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer' }}
        data-testid="scan-open-camera-btn">
        Open camera
      </button>
    </div>

    {/* Divider */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
      <span style={{ fontSize: 11, color: '#ccc' }}>or upload a file</span>
      <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
    </div>

    {/* Upload Zone */}
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => fileRef.current?.click()}
      style={{
        border: `1.5px dashed ${dragOver ? '#999' : '#d1d5db'}`,
        borderRadius: 10, padding: '20px', textAlign: 'center', cursor: 'pointer',
        background: dragOver ? '#f9f9f9' : '#fff', transition: 'all 0.15s',
      }}
      data-testid="scan-tab-dropzone">
      {file ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Check style={{ width: 16, height: 16, color: '#16a34a' }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{file.name}</span>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 13, fontWeight: 500, color: '#111', margin: '0 0 3px' }}>Drop a file here or click to browse</p>
          <p style={{ fontSize: 10, color: '#bbb', marginTop: 4 }}>Accepted: .jpg, .jpeg, .png, .pdf, .heic</p>
        </>
      )}
      <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf,.heic" onChange={onFileChange} style={{ display: 'none' }}
        data-testid="scan-tab-file-input" />
    </div>

    {/* Sample file */}
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
      <span style={{ fontSize: 11, color: '#ccc' }}>or try a sample file</span>
      <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
    </div>
    <div>
      <p style={{ fontSize: 9, textTransform: 'uppercase', color: '#aaa', letterSpacing: '.05em', marginBottom: 6 }}>Sample files</p>
      <SampleRow
        icon={<FileText style={{ width: 14, height: 14, color: '#6b5ce7' }} />}
        iconBg="#ede9fe"
        name="sample_id_card.pdf"
        meta="Fictional patient ID card &middot; PDF &middot; 1 KB"
        onTry={() => onLoadSample('sample_id_card.pdf')} />
    </div>
  </>
);

/* ── Shared: Sample File Row ── */
const SampleRow = ({ icon, iconBg, name, meta, onTry }) => (
  <div style={{
    background: '#fafafa', border: '0.5px solid #ebebeb', borderRadius: 8, padding: '8px 12px',
    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
  }}
    onClick={onTry}
    data-testid={`sample-file-${name}`}>
    <div style={{ width: 28, height: 28, borderRadius: 6, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ fontSize: 12, fontWeight: 500, color: '#111', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</p>
      <p style={{ fontSize: 10, color: '#aaa', margin: '2px 0 0' }} dangerouslySetInnerHTML={{ __html: meta }} />
    </div>
    <span style={{ fontSize: 11, color: '#6b5ce7', fontWeight: 500, flexShrink: 0 }}>Try this &rarr;</span>
  </div>
);

export default ImportPatientsModal;
